import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Search } from "lucide-react";
import { api, errMsg, fmt } from "../lib/api";
import { useApi } from "../lib/settings";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { GameForm } from "./GameForm";

export default function AdminGames() {
  const cats = useApi("/categories", []);
  const categories = Array.isArray(cats.data) ? cats.data : [];
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [feat, setFeat] = useState("all");
  const [page, setPage] = useState(1);
  const [res, setRes] = useState({ items: [], total: 0, pages: 1 });
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    const params = { page, limit: 25 };
    if (q) params.q = q;
    if (cat !== "all") params.category = cat;
    if (feat !== "all") params.featured = feat === "yes";
    try {
      const r = await api.get("/admin/games", { params });
      setRes({ items: r.data?.items || [], total: r.data?.total || 0, pages: r.data?.pages || 1 });
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [q, cat, feat, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const patch = async (slug, data) => {
    try {
      await api.patch(`/admin/games/${slug}`, data);
      setRes((r) => ({ ...r, items: r.items.map((g) => (g.slug === slug ? { ...g, ...data } : g)) }));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (g) => {
    if (!window.confirm(`Eliminare "${g.title}"?`)) return;
    await api.delete(`/admin/games/${g.slug}`);
    toast.success("Gioco eliminato");
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-games">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Oswald" }}>Giochi <span className="text-slate-300 text-lg">({res.total.toLocaleString("it-IT")})</span></h1>
        <Button onClick={() => setEdit({})} className="bg-[#fc0] text-[#043856] hover:bg-yellow-300 font-bold" data-testid="admin-add-game-btn"><Plus size={16} /> Aggiungi gioco</Button>
      </div>
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Cerca per titolo..." className="pl-9 bg-white/5 text-white border-white/15" data-testid="admin-games-search" />
        </div>
        <Select value={cat} onValueChange={(v) => { setCat(v); setPage(1); }}>
          <SelectTrigger className="w-48 bg-white/5 text-white border-white/15" data-testid="admin-games-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={feat} onValueChange={(v) => { setFeat(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-white/5 text-white border-white/15" data-testid="admin-games-featured-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="yes">In evidenza</SelectItem>
            <SelectItem value="no">Non in evidenza</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#0d2233] overflow-x-auto">
        <table className="w-full text-sm text-slate-200">
          <thead className="text-xs uppercase text-slate-300 border-b border-white/10">
            <tr><th className="p-3 text-left">Gioco</th><th className="p-3 text-left">Categoria</th><th className="p-3">Partite</th><th className="p-3">Pubblicato</th><th className="p-3">In evidenza</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {res.items.map((g) => (
              <tr key={g.slug} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-game-row-${g.slug}`}>
                <td className="p-3"><div className="flex items-center gap-3"><img src={g.image} alt="" className="w-14 h-10 object-cover rounded" /><a href={`/gioco/${g.slug}`} target="_blank" rel="noreferrer" className="text-white hover:text-[#fc0]">{g.title}</a></div></td>
                <td className="p-3">{categories.find((c) => c.slug === g.category)?.name || g.category}</td>
                <td className="p-3 text-center">{fmt(g.plays)}</td>
                <td className="p-3 text-center"><Switch checked={!!g.published} onCheckedChange={(v) => patch(g.slug, { published: v })} data-testid={`admin-publish-${g.slug}`} /></td>
                <td className="p-3 text-center"><Switch checked={!!g.featured} onCheckedChange={(v) => patch(g.slug, { featured: v })} data-testid={`admin-feature-${g.slug}`} /></td>
                <td className="p-3"><div className="flex gap-1 justify-end">
                  <Button size="icon" variant="ghost" onClick={() => setEdit(g)} className="text-slate-200 hover:text-white hover:bg-white/10" data-testid={`admin-edit-${g.slug}`}><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(g)} className="text-red-300 hover:text-red-200 hover:bg-white/10" data-testid={`admin-delete-${g.slug}`}><Trash2 size={15} /></Button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-center gap-3 text-slate-200">
        <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="bg-transparent border-white/15" data-testid="admin-games-prev">Precedente</Button>
        <span className="text-sm">Pagina {page} di {res.pages}</span>
        <Button variant="outline" disabled={page >= res.pages} onClick={() => setPage(page + 1)} className="bg-transparent border-white/15" data-testid="admin-games-next">Successiva</Button>
      </div>
      {edit && <GameForm game={edit} categories={categories} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
